'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var baseui = require('baseui');
var deepMerge = require('./utils/deep-merge.js');

var primaryFontFamily = 'Roboto, system-ui, "Helvetica Neue", Helvetica, Arial, sans-serif';
var headerFontFamily = 'Urbanist, system-ui, "Helvetica Neue", Helvetica, Arial, sans-serif';
var primary = '#FB9A44';
var themePrimitives = {
  primaryFontFamily: primaryFontFamily,
  primary: primary
};
var colors = {
  primary100: '#FFFCF9',
  primary200: '#FFFAF6',
  primary300: '#FFF5EC',
  primary400: '#FEEBDA',
  primary500: '#FDD7B4',
  primary600: '#FDC28F',
  primary700: '#FCAE69',
  primary800: '#FB9A44',
  primary900: '#EE9240',
  primary1000: '#E8842C',
  secondary100: '#FAFCFE',
  secondary200: '#F4F8FD',
  secondary300: '#E8F1FB',
  secondary400: '#D1E4F6',
  secondary500: '#A3C8ED',
  secondary600: '#75ADE4',
  secondary700: '#4791DB',
  secondary800: '#1976D2',
  secondary900: '#1564B1',
  secondary1000: '#115AA0',
  gray50: '#F4F5F6',
  gray100: '#EAECEF',
  gray200: '#D4DADF',
  gray300: '#BFC7CF',
  gray400: '#AAB5BF',
  gray500: '#94A2AF',
  gray600: '#7F909F',
  gray700: '#6A7D8F',
  gray800: '#546A80',
  gray900: '#3F5870',
  gray1000: '#294560',
  black01: '#222222',
  black02: '#656565',
  black03: '#767676',
  backgroundPrimary: '#FAFCFE',
  backgroundBanner: '#FFFFFE',
  bodyText: '#222222',
  subText: '#656565',
  hintMessage: '#767676',
  heading: '#294560',
  subTitle: '#3F5870',
  primaryAction: '#FB9A44',
  primaryHover: '#FFF5EC',
  secondaryLinkPressed: '#115AA0',
  secondaryLink: '#1564B1',
  secondaryAction: '#1976D2',
  secondaryHover: '#E8F1FB',
  border: '#AAB5BF',
  divider: '#D4DADF',
  positive: '#30A442',
  positiveLight: '#D6EDD9',
  error: '#B93535',
  errorLight: '#F1D7D7',
  warning: '#E4A70A',
  warningLight: '#FAEDCE',
  information: '#0A88E4',
  informationLight: '#CEE7FA',
  outline: '#1976D2',
  // the same as borderSelected
  accent: '#1976D2',
  contentAccent: '#1976D2',
  contentPrimary: '#222222',
  // general override
  borderOpaque: '#222',
  borderSelected: '#1976D2',
  borderNegative: '#B93535',
  // Link
  linkText: '#1564B1',
  linkVisited: '#1564B1',
  linkHover: '#1564B1',
  linkActive: '#115AA0',
  // Input
  inputBorder: '#AAB5BF',
  inputBorderError: '#B93535',
  inputBorderPositive: '#30A442',
  inputFill: '#fff',
  inputFillActive: '#fff',
  inputFillPositive: '#fff',
  inputFillError: '#F1D7D7',
  inputFillDisabled: '#D4DADF',
  inputPlaceholder: '#767676',
  inputTextDisabled: '#767676',
  // Checkbox/Toggle
  // for check box status and indeterminate state, keep baseui style
  tickBorder: '#7F909F',
  tickFill: '#fff',
  tickFillHover: '#D4DADF',
  tickFillActive: '#1976D2',
  tickFillSelected: '#1976D2',
  tickFillSelectedHover: '#1564B1',
  tickFillSelectedHoverActive: '#1564B1',
  tickFillDisabled: '#D4DADF',
  // Tag
  tagNeutralSolidFont: '#3F5870',
  tagNeutralSolidBackground: '#F1F2F4',
  tagAccentSolidFont: '#1564B1',
  tagAccentSolidBackground: '#F1F2F4',
  tagPositiveSolidFont: '#1F6C2B',
  tagPositiveSolidBackground: '#D6EDD9',
  tagNegativeSolidFont: '#A43030',
  tagNegativeSolidBackground: '#F1D7D7',
  tagWarningSolidFont: '#846107',
  tagWarningSolidBackground: '#FDF5E7',
  // modal
  modalCloseColor: '#3F5870',
  modalCloseColorHover: '#294560'
};
var borders = {
  inputBorderRadius: '5px'
};
var typography = {
  DisplayLarge: {
    fontSize: '96px',
    fontWeight: 700,
    lineHeight: '112px',
    fontFamily: headerFontFamily
  },
  DisplayMedium: {
    fontSize: '64px',
    fontWeight: 700,
    lineHeight: '80px',
    fontFamily: headerFontFamily
  },
  DisplaySmall: {
    fontSize: '48px',
    fontWeight: 700,
    lineHeight: '64px',
    fontFamily: headerFontFamily
  },
  HeadingXLarge: {
    fontSize: '40px',
    fontWeight: 700,
    lineHeight: '48px',
    fontFamily: headerFontFamily
  },
  HeadingLarge: {
    fontSize: '32px',
    fontWeight: 700,
    lineHeight: '40px',
    fontFamily: headerFontFamily
  },
  HeadingMedium: {
    fontSize: '24px',
    fontWeight: 600,
    lineHeight: '40px',
    fontFamily: headerFontFamily
  },
  HeadingSmall: {
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: '32px',
    fontFamily: headerFontFamily
  },
  HeadingSmallBold: {
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: '32px',
    fontFamily: headerFontFamily
  },
  HeadingMenu: {
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
    fontFamily: headerFontFamily
  },
  HeadingMenuSelected: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    fontFamily: headerFontFamily
  },
  Subtitle: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    fontFamily: primaryFontFamily
  },
  Body1: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    fontFamily: primaryFontFamily
  },
  Body2: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '16px',
    fontFamily: primaryFontFamily
  },
  Label: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '16px',
    fontFamily: primaryFontFamily
  },
  Action: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    fontFamily: primaryFontFamily
  },
  ButtonText: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    fontFamily: primaryFontFamily
  }
};
var breakpoints = {
  small: 320,
  medium: 1024,
  large: 1440
};
var mediaQuery = {
  small: '@media screen and (min-width: 320px)',
  medium: '@media screen and (min-width: 1024px)',
  large: '@media screen and (min-width: 1440px)'
};
var themeOverrides = {
  colors: colors,
  borders: borders,
  typography: typography,
  breakpoints: breakpoints,
  mediaQuery: mediaQuery
}; // custom theme: extended colors and typography

function createTheme(primitives, overrides) {
  var theme = baseui.createLightTheme(deepMerge({}, themePrimitives, primitives), deepMerge({}, themeOverrides, overrides));
  return theme;
}
var useStyletron = baseui.createThemedUseStyletron();
var withStyle = baseui.createThemedWithStyle();
var styled = baseui.createThemedStyled();

exports.createTheme = createTheme;
exports.headerFontFamily = headerFontFamily;
exports.styled = styled;
exports.themeOverrides = themeOverrides;
exports.themePrimitives = themePrimitives;
exports.useStyletron = useStyletron;
exports.withStyle = withStyle;
